import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import Drawer from '@app/components/Drawer';
import KpiCard from '@app/components/KpiCard';
import ListWorkspace, { type ListWorkspaceColumn } from '@app/components/ListWorkspace';
import { PlusIcon } from '@app/components/icons';
import { useConfig } from '@config/provider';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useChangeValidationsApi, useChatRunsApi } from '@app/hooks/useApis';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import type {
  ArtifactDocument,
  ChatRunsApiClass,
  ChangeValidation,
  ChangeValidationsApiClass,
  ChangeValidationComparisonschema,
  ChangeValidationListResponse,
  ChangeValidationObservationschema,
  ChangeValidationReport,
  ChangeValidationReportSummaryschema
} from '@app/types/sdk';
import { formatDateTime, formatNumber } from '@app/utils/format';
import { ApiResponseError, buildOrgUrl, resolveBasePath } from '@app/utils/api';
import { summarizeRunEvent } from '@app/utils/chatRunTrace';
import { buildOrgScopedPath } from '@app/utils/org-routes';
import { streamSse } from '@app/utils/sse';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';

type ChangeBucket = 'all' | 'needs_action' | 'in_progress' | 'completed';
type CollectionStage = 'pre' | 'post';
type ChangeAction = 'approve-plan' | CollectionStage;
type NewChangeProgressPhase = 'running' | 'finalizing' | 'needs_input' | 'ready' | 'failed' | 'disconnected';

type NewChangeProgress = {
  threadId: string;
  runId?: string;
  validationId?: string;
  planApprovalObserved?: boolean;
  phase: NewChangeProgressPhase;
  detail: string;
  activity: string[];
  runFinished: boolean;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const MAX_VALIDATIONS = 100;
const COLLECTION_POLL_INTERVAL_MS = 2_000;

const NEEDS_ACTION_STATUSES = new Set(['draft', 'needs_input', 'plan_ready', 'approved', 'pre_complete', 'awaiting_change', 'failed']);
const IN_PROGRESS_STATUSES = new Set(['pre_running', 'post_running']);
const COMPLETED_STATUSES = new Set(['completed', 'cancelled']);

const changeValidationIdempotencyKey = (action: ChangeAction): string => {
  const id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `change-validation-${action}-${id}`;
};

const newChangeClientRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `change-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const chatRunIdFromLocation = (location: string | null): string | null => {
  if (!location) {
    return null;
  }
  return location.match(/chat\/runs\/([^/?]+)/i)?.[1] ?? null;
};

const sourceChatThreadId = (validation: ChangeValidation): string | null => {
  const value = validation.source.threadId;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

const appendNewChangeActivity = (activity: string[], detail: string): string[] => {
  const normalized = detail.trim();
  if (!normalized || activity.at(-1) === normalized) {
    return activity;
  }
  return [...activity, normalized].slice(-6);
};

export const changeValidationPlanApprovalFromRunEvent = (
  eventType: string,
  payload: Record<string, unknown>
): { validationId?: string } | null => {
  if (eventType !== 'progress' || !payload.meta || typeof payload.meta !== 'object') {
    return null;
  }
  const meta = payload.meta as Record<string, unknown>;
  if (meta.approval_required !== true) {
    return null;
  }
  const approvalKey = typeof meta.approval_key === 'string' ? meta.approval_key.trim() : '';
  const isChangeValidationApproval =
    approvalKey.startsWith('change-validation-plan:') ||
    typeof meta.change_validation_plan === 'string' ||
    typeof meta.validation_id === 'string';
  if (!isChangeValidationApproval) {
    return null;
  }
  const validationId = typeof meta.validation_id === 'string' ? meta.validation_id.trim() : '';
  return validationId ? { validationId } : {};
};

const newChangeProgressTitle = (phase: NewChangeProgressPhase): string => {
  switch (phase) {
    case 'running': return 'Creating validation plan';
    case 'finalizing': return 'Finalizing validation';
    case 'needs_input': return 'More information is needed';
    case 'ready': return 'Validation plan ready';
    case 'failed': return 'Unable to create validation';
    case 'disconnected': return 'Progress updates paused';
  }
};

export const newChangeConversationPrompt = (description: string): string => {
  const text = description.trim();
  if (/^create\s+(?:a\s+)?change validation plan\b/i.test(text)) {
    return text;
  }
  return `Create a change validation plan for the following proposed change:\n\n${text}`;
};

const newChangeConversationTitle = (description: string): string => {
  const firstLine = description.trim().replace(/\s+/g, ' ').split(/[.!?](?:\s|$)/, 1)[0] || 'New change';
  const title = /^change\b/i.test(firstLine) ? firstLine : `Change validation: ${firstLine}`;
  return title.length > 96 ? `${title.slice(0, 95).trimEnd()}…` : title;
};

const responseStatus = (error: unknown): number | null => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return null;
  }
  const response = (error as { response?: unknown }).response;
  return response instanceof Response ? response.status : null;
};

export const changeManagementErrorMessage = (error: Error): string => {
  if (responseStatus(error) === 403) {
    return 'You do not have permission to view change validations in this organization. Ask an administrator for change-validation read access, then sign out and back in.';
  }
  return `Unable to load change validations. ${error.message}`;
};

const humanize = (value?: string | null): string => {
  const normalized = value?.trim();
  if (!normalized) {
    return '—';
  }
  return normalized
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

type ValidationPlanDocument = NonNullable<NonNullable<ChangeValidation['currentPlan']>['document']>;
type ValidationPlanTestcase = ValidationPlanDocument['automaticTestcases'][number];
type PreReadiness = NonNullable<NonNullable<ChangeValidation['latestPreStageRun']>['preReadiness']>;

const formatPlanPhase = (phase: string): string => phase === 'both' ? 'Before and after change' : humanize(phase);

const formatPlanValue = (value: unknown): string => {
  if (value === undefined || value === null) {
    return 'Not specified';
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

const planTestcaseScope = (testcase: ValidationPlanTestcase): string => {
  const parts = [
    testcase._interface && `Interface ${testcase._interface}`,
    testcase.vlan && `VLAN ${testcase.vlan}`,
    testcase.vrf && `VRF ${testcase.vrf}`,
    testcase.prefix && `Prefix ${testcase.prefix}`,
    testcase.subnet && `Subnet ${testcase.subnet}`,
    testcase.policyDomain && `Policy domain ${testcase.policyDomain}`,
    testcase.policy && `Policy ${testcase.policy}`,
    testcase.rule && `Rule ${testcase.rule}`,
    testcase.natRule && `NAT rule ${testcase.natRule}`,
    testcase.acl && `ACL ${testcase.acl}`,
    testcase.ace && `ACE ${testcase.ace}`,
    testcase.attachment && `Attachment ${testcase.attachment}`,
    testcase.direction && `Direction ${testcase.direction}`,
    testcase.protocol && `Protocol ${testcase.protocol}`,
    testcase.destinationPort ? `Port ${testcase.destinationPort}` : undefined
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : 'Device-level validation';
};

const PlanReview = ({ validation, document }: { validation: ChangeValidation; document: ValidationPlanDocument }) => {
  const targets = changeTargetNames(validation);
  return (
    <section className="change-validation-plan" aria-label="Change validation plan">
      <div className="change-validation-plan__header">
        <div>
          <strong>{validation.status === 'plan_ready' ? 'Plan awaiting approval' : `Validation plan v${document.version}`}</strong>
          <span>{humanize(document.profileId)}</span>
        </div>
        <span className="change-validation-plan__count">
          {document.automaticTestcases.length} automatic {document.automaticTestcases.length === 1 ? 'check' : 'checks'}
        </span>
      </div>
      {targets.length > 0 ? (
        <div className="change-validation-plan__target">
          <span>{targets.length === 1 ? 'Target' : 'Targets'}</span>
          <strong>{targets.join(', ')}</strong>
        </div>
      ) : null}
      <div className="change-validation-plan__checks">
        {document.automaticTestcases.map((testcase) => (
          <article className="change-validation-plan__check" key={testcase.testcaseId}>
            <div className="change-validation-plan__check-title">
              <strong>{humanize(testcase.testcaseId)}</strong>
              <span>{formatPlanPhase(testcase.phase)}</span>
            </div>
            <p>{planTestcaseScope(testcase)}</p>
            <dl>
              <div><dt>Before</dt><dd>{formatPlanValue(testcase.requiredPreValue)}</dd></div>
              <div><dt>Expected after</dt><dd>{formatPlanValue(testcase.requiredPostValue)}</dd></div>
              <div><dt>Failure impact</dt><dd>{testcase.blocking ? `Blocking · ${humanize(testcase.severity)}` : humanize(testcase.severity)}</dd></div>
            </dl>
          </article>
        ))}
      </div>
      <div className="change-validation-plan__footer">
        <details>
          <summary>Technical details</summary>
          <dl>
            <div><dt>Plan ID</dt><dd>{document.planId}</dd></div>
            {validation.currentPlan?.contentHash ? <div><dt>Content hash</dt><dd>{validation.currentPlan.contentHash}</dd></div> : null}
            <div><dt>Profile</dt><dd>{document.profileId}@{document.profileVersion}</dd></div>
            {document.automaticTestcases.map((testcase) => (
              <div key={`technical-${testcase.testcaseId}`}>
                <dt>{humanize(testcase.testcaseId)} collection</dt>
                <dd>{humanize(testcase.sourceFamily)} · {testcase.timeoutSeconds}s timeout · {testcase.retries} retry</dd>
              </div>
            ))}
          </dl>
        </details>
      </div>
    </section>
  );
};

export const mergeChangeValidationDetail = (
  current: ChangeValidation | null,
  refreshed: ChangeValidation
): ChangeValidation => {
  if (!current || current.id !== refreshed.id) {
    return refreshed;
  }
  const samePlan = current.currentPlan && refreshed.currentPlan
    && current.currentPlan.planId === refreshed.currentPlan.planId
    && current.currentPlan.version === refreshed.currentPlan.version;
  if (!samePlan || !current.currentPlan?.document || !refreshed.currentPlan) {
    return refreshed;
  }
  return {
    ...refreshed,
    currentPlan: {
      ...refreshed.currentPlan,
      contentHash: refreshed.currentPlan.contentHash ?? current.currentPlan.contentHash,
      document: refreshed.currentPlan.document ?? current.currentPlan.document
    }
  };
};

export const changeBucketForStatus = (status: string): Exclude<ChangeBucket, 'all'> => {
  if (IN_PROGRESS_STATUSES.has(status)) {
    return 'in_progress';
  }
  if (COMPLETED_STATUSES.has(status)) {
    return 'completed';
  }
  return 'needs_action';
};

export const changeStage = (validation: ChangeValidation): { completed: number; label: string } => {
  switch (validation.status) {
    case 'draft':
      return { completed: 0, label: 'Draft' };
    case 'needs_input':
      return { completed: 0, label: 'Needs input' };
    case 'plan_ready':
      return { completed: 1, label: 'Confirm plan' };
    case 'approved':
      return { completed: 2, label: 'Run pre-checks' };
    case 'pre_running':
      return { completed: 2, label: 'Pre-checks running' };
    case 'pre_complete':
    case 'awaiting_change':
      return { completed: 3, label: 'Run post-checks' };
    case 'post_running':
      return { completed: 3, label: 'Post-checks running' };
    case 'completed':
      return { completed: 5, label: 'Verdict' };
    case 'failed': {
      const completed = validation.latestPostStageRun
        ? 4
        : validation.latestPreStageRun
          ? 3
          : validation.currentPlan?.status === 'approved'
            ? 2
            : validation.currentPlan
              ? 1
              : 0;
      return { completed, label: 'Needs attention' };
    }
    case 'cancelled': {
      const completed = validation.latestPostStageRun
        ? 4
        : validation.latestPreStageRun
          ? 3
          : validation.currentPlan?.status === 'approved'
            ? 2
            : validation.currentPlan
              ? 1
              : 0;
      return { completed, label: 'Cancelled' };
    }
    default:
      return { completed: 0, label: humanize(validation.status) };
  }
};

const sourceLabel = (validation: ChangeValidation): string => {
  if (validation.source.kind === 'ticket') {
    return validation.source.provider?.trim() || 'Ticket';
  }
  return humanize(validation.source.kind);
};

const changeReference = (validation: ChangeValidation): string =>
  validation.source.ticketId?.trim() || `REAP-${validation.id.slice(0, 8).toUpperCase()}`;

const isUuid = (value: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const targetValueKeys = /^(device_?name|hostname|display_?name|target_?name)$/i;
const targetContainerKeys = /^(devices?|targets?|scope)$/i;

const collectTargetNames = (value: unknown, output: Set<string>, parentKey = '', depth = 0): void => {
  if (depth > 4 || value == null || output.size >= 8) {
    return;
  }
  if (typeof value === 'string') {
    const candidate = value.trim();
    if (candidate && !isUuid(candidate) && (targetValueKeys.test(parentKey) || targetContainerKeys.test(parentKey))) {
      output.add(candidate);
    }
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((entry) => collectTargetNames(entry, output, parentKey, depth + 1));
    return;
  }
  if (typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([key, entry]) => {
      if (targetValueKeys.test(key) || targetContainerKeys.test(key) || typeof entry === 'object') {
        collectTargetNames(entry, output, key, depth + 1);
      }
    });
  }
};

export const changeTargetNames = (validation: ChangeValidation): string[] => {
  const targets = new Set<string>();
  collectTargetNames(validation.targetOverrides, targets);
  Object.entries(validation.labels ?? {}).forEach(([key, value]) => collectTargetNames(value, targets, key));

  if (targets.size === 0) {
    const patterns = [
      /device\s+name\s*:\s*([^.;,\n]+)/i,
      /device\s*:\s*([^.;,\n]+)/i,
      /\bon\s+device\s+([^.;,\n]+)/i
    ];
    for (const pattern of patterns) {
      const match = validation.summary.match(pattern)?.[1]?.trim();
      if (match && !isUuid(match)) {
        targets.add(match);
        break;
      }
    }
  }
  return Array.from(targets);
};

const verdictLabel = (verdict?: string): string => {
  switch (verdict) {
    case 'pass':
      return 'Pass';
    case 'warn':
      return 'Pass with warnings';
    case 'fail':
      return 'Fail';
    case 'inconclusive':
      return 'Inconclusive';
    default:
      return 'Pending';
  }
};

const badgeTone = (value?: string): string => {
  if (value === 'pass' || value === 'succeeded' || value === 'completed' || value === 'ready') {
    return 'ui-badge ui-badge--success';
  }
  if (value === 'fail' || value === 'failed' || value === 'blocked') {
    return 'ui-badge ui-badge--danger';
  }
  if (value === 'warn' || value === 'attention' || value === 'inconclusive' || value === 'needs_input' || value === 'needs_attention') {
    return 'ui-badge ui-badge--warning';
  }
  return 'ui-badge ui-badge--ghost';
};

const displayValue = (value: unknown): string => {
  if (value == null || value === '') {
    return '—';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value, null, 2);
};

const collectedCommands = (observation: ChangeValidationObservationschema): string[] => {
  const actions = observation.collector.executed_actions;
  if (!Array.isArray(actions)) {
    return [];
  }
  return actions.flatMap((action) => {
    if (!action || typeof action !== 'object') {
      return [];
    }
    const value = (action as Record<string, unknown>).command ?? (action as Record<string, unknown>).query;
    return typeof value === 'string' && value.trim() ? [value.trim()] : [];
  });
};

const artifactDocumentContent = (document: ArtifactDocument): Record<string, unknown> => {
  const preferredView = document.presentationHints?.defaultView;
  const view = preferredView ? document.views?.find((candidate) => candidate.rep === preferredView) : undefined;
  return (view?.content ?? document.content) as unknown as Record<string, unknown>;
};

const EvidenceArtifactOutput = ({
  api,
  artifactId,
  orgId
}: {
  api: ChatRunsApiClass;
  artifactId: string;
  orgId: string;
}) => {
  const [document, setDocument] = useState<ArtifactDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingFull, setLoadingFull] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDocument(null);
    setError(null);
    setLoading(true);
    api.getChatArtifactById({ orgId, artifactId }, { cache: 'no-store' })
      .then((result) => { if (!cancelled) setDocument(result); })
      .catch((caught) => {
        if (!cancelled) setError(caught instanceof Error ? caught.message : 'Unable to load evidence output.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, artifactId, orgId]);

  const loadFullOutput = async () => {
    setLoadingFull(true);
    setError(null);
    try {
      setDocument(await api.getChatArtifactById({ orgId, artifactId, content: 'full' }, { cache: 'no-store' }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to load the full evidence output.');
    } finally {
      setLoadingFull(false);
    }
  };

  if (loading) {
    return <div className="change-management-evidence__artifact-state">Loading collected output…</div>;
  }
  if (!document) {
    return <div className="change-management-evidence__artifact-error">Collected output unavailable. {error}</div>;
  }

  const content = artifactDocumentContent(document);
  const type = typeof content.type === 'string' ? content.type : document.kind;
  const title = document.title || document.summary || 'Collected output';

  if (type === 'table') {
    const rows = Array.isArray(content.rows) ? content.rows.filter((row): row is Record<string, unknown> => Boolean(row) && typeof row === 'object') : [];
    const columns = Array.isArray(content.columns)
      ? content.columns.filter((column): column is string => typeof column === 'string')
      : Array.from(new Set(rows.flatMap((row) => Object.keys(row))));
    const visibleRows = rows.slice(0, 10);
    return (
      <div className="change-management-evidence__artifact">
        <strong>{title}</strong>
        <div className="chat-message__inline-table change-management-evidence__artifact-table">
          <div className="chat-message__inline-table-wrap">
            <table>
              <thead><tr>{columns.map((column) => <th key={`${artifactId}-${column}`}>{column}</th>)}</tr></thead>
              <tbody>
                {visibleRows.map((row, rowIndex) => (
                  <tr key={`${artifactId}-row-${rowIndex}`}>
                    {columns.map((column) => <td key={`${artifactId}-${rowIndex}-${column}`}>{displayValue(row[column])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {rows.length > visibleRows.length ? <small>Showing 10 of {rows.length} rows.</small> : null}
      </div>
    );
  }

  if (type === 'commandOutput') {
    const fullText = typeof content.fullText === 'string' ? content.fullText : '';
    const previewText = typeof content.previewText === 'string' ? content.previewText : '';
    const output = fullText || previewText;
    const truncated = content.truncated === true && !fullText;
    return (
      <div className="change-management-evidence__artifact">
        <strong>{title}</strong>
        {typeof content.command === 'string' && content.command.trim() ? <code>{content.command}</code> : null}
        <pre className="chat-message__inline-terminal">{output || 'No output was returned.'}</pre>
        {truncated ? (
          <button type="button" className="ui-button ui-button--ghost" disabled={loadingFull} onClick={() => void loadFullOutput()}>
            {loadingFull ? 'Loading full output…' : 'Show full output'}
          </button>
        ) : null}
        {error ? <div className="change-management-evidence__artifact-error">{error}</div> : null}
      </div>
    );
  }

  return (
    <div className="change-management-evidence__artifact">
      <strong>{title}</strong>
      <pre className="change-management-evidence__artifact-json">{JSON.stringify(content, null, 2)}</pre>
    </div>
  );
};

const ObservationArtifactOutputs = ({
  api,
  artifactIds,
  orgId
}: {
  api: ChatRunsApiClass;
  artifactIds: string[];
  orgId: string;
}) => artifactIds.length > 0 ? (
  <div className="change-management-evidence__artifacts">
    <span>Collected output</span>
    {artifactIds.map((artifactId) => <EvidenceArtifactOutput key={artifactId} api={api} artifactId={artifactId} orgId={orgId} />)}
  </div>
) : null;

const ObservationEvidence = ({
  artifactApi,
  orgId,
  title,
  emptyMessage,
  observations
}: {
  artifactApi: ChatRunsApiClass;
  orgId: string;
  title: string;
  emptyMessage: string;
  observations: ChangeValidationObservationschema[];
}) => (
  <section className="change-management-evidence">
    <div className="change-management-evidence__heading">
      <h3>{title}</h3>
      {observations.length > 0 ? <span>{observations.length} observation{observations.length === 1 ? '' : 's'}</span> : null}
    </div>
    {observations.length === 0 ? <p className="change-management-detail__empty">{emptyMessage}</p> : (
      <div className="change-management-evidence__list">
        {observations.map((observation) => {
          const values = Object.entries(observation.semanticValues);
          const commands = collectedCommands(observation);
          return (
            <article key={observation.observationId} className="change-management-evidence__card">
              <div className="change-management-evidence__card-heading">
                <div>
                  <strong>{observation.testcaseName || humanize(observation.testcaseId)}</strong>
                  <small>{observation.targetKey}</small>
                </div>
                <span className={badgeTone(observation.status)}>{humanize(observation.status)}</span>
              </div>
              {values.length > 0 ? (
                <dl className="change-management-evidence__values">
                  {values.map(([semantic, value]) => (
                    <div key={semantic}>
                      <dt>{humanize(semantic)}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              ) : <p className="change-management-detail__empty">No normalized values were captured.</p>}
              {commands.length > 0 ? (
                <div className="change-management-evidence__commands">
                  <span>Collected with</span>
                  {commands.map((command, index) => <code key={`${command}-${index}`}>{command}</code>)}
                </div>
              ) : null}
              <ObservationArtifactOutputs api={artifactApi} artifactIds={observation.artifactIds} orgId={orgId} />
              {observation.error ? <div className="change-management-evidence__error">{displayValue(observation.error.summary ?? observation.error)}</div> : null}
              <div className="change-management-evidence__meta">
                <span>{humanize(observation.sourceFamily)}</span>
                <span>Captured {formatDateTime(observation.collectedAt)}</span>
                {observation.artifactIds.length > 0 ? <span>{observation.artifactIds.length} artifact{observation.artifactIds.length === 1 ? '' : 's'}</span> : null}
              </div>
            </article>
          );
        })}
      </div>
    )}
  </section>
);

const comparisonSupplementaryDetails = (comparison: ChangeValidationComparisonschema): Array<[string, unknown]> =>
  Object.entries(comparison.details).filter(([key]) => key !== 'actual_pre' && key !== 'actual_post');

const ComparisonEvidence = ({ comparisons }: { comparisons: ChangeValidationComparisonschema[] }) => (
  <section className="change-management-evidence">
    <div className="change-management-evidence__heading">
      <h3>Pre/post comparison</h3>
      <span>{comparisons.length} result{comparisons.length === 1 ? '' : 's'}</span>
    </div>
    {comparisons.length === 0 ? <p className="change-management-detail__empty">No comparison results are available yet.</p> : (
      <div className="change-management-evidence__list">
        {comparisons.map((comparison) => (
          <article key={comparison.comparisonId} className="change-management-evidence__card">
            <div className="change-management-evidence__card-heading">
              <div>
                <strong>{humanize(comparison.testcaseId)}</strong>
                <small>{comparison.targetKey}</small>
              </div>
              <span className={badgeTone(comparison.verdict)}>{humanize(comparison.verdict)}</span>
            </div>
            <p>{comparison.summary}</p>
            <dl className="change-management-evidence__values" aria-label={`${humanize(comparison.testcaseId)} expected and observed values`}>
              <div><dt>Pre expected</dt><dd>{displayValue(comparison.expectation.requiredPreValue)}</dd></div>
              <div><dt>Pre observed</dt><dd>{displayValue(comparison.details.actual_pre)}</dd></div>
              <div><dt>Post expected</dt><dd>{displayValue(comparison.expectation.requiredPostValue)}</dd></div>
              <div><dt>Post observed</dt><dd>{displayValue(comparison.details.actual_post)}</dd></div>
            </dl>
            {comparisonSupplementaryDetails(comparison).length > 0 ? (
              <details className="change-management-detail__technical">
                <summary>Comparison metadata</summary>
                <dl className="change-management-evidence__values">
                  {comparisonSupplementaryDetails(comparison).map(([key, value]) => (
                  <div key={key}><dt>{humanize(key)}</dt><dd>{displayValue(value)}</dd></div>
                  ))}
                </dl>
              </details>
            ) : null}
          </article>
        ))}
      </div>
    )}
  </section>
);

type ValidationSummaryRow = {
  key: string;
  label: string;
  description: string;
  target: string;
  preStatus: string;
  preExpected?: string;
  preObserved?: string;
  preReason?: string;
  postStatus: string;
  result: string;
};

const validationTestKey = (testcaseId: string, targetKey: string): string => `${testcaseId}\u0000${targetKey}`;

const validationTestDescriptions: Record<string, string> = {
  acl_entry_effect: 'Confirm that the selected ACL entry has the intended permit or deny effect.',
  firewall_rule_effect: 'Confirm that the selected firewall rule has the intended action.',
  interface_admin_state: 'Confirm that the interface administrative state matches the intended state.',
  nat_rule_translation: 'Confirm that the selected NAT rule has the intended translation.',
  route_presence: 'Confirm that route presence matches the intended routing change.',
  svi_oper_state: 'Confirm that the SVI has the intended operational state.',
  vlan_interface_oper_state: 'Confirm that the trunk interface remains operational.',
  vlan_local_state: 'Confirm that the VLAN remains active on the device.',
  vlan_membership_state: 'Confirm that VLAN membership matches the intended trunk configuration.'
};

const decodeTargetKeyPart = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const parseValidationTargetKey = (targetKey: string): Record<string, string> | null => {
  if (!targetKey.startsWith('tk1|')) {
    return null;
  }
  return targetKey.split('|').slice(1).reduce<Record<string, string>>((fields, part) => {
    const separator = part.indexOf('=');
    if (separator > 0) {
      fields[part.slice(0, separator)] = decodeTargetKeyPart(part.slice(separator + 1));
    }
    return fields;
  }, {});
};

export const friendlyValidationTarget = (targetKey: string, validation: ChangeValidation): string => {
  const targetNames = changeTargetNames(validation);
  const fields = parseValidationTargetKey(targetKey);
  if (!fields) {
    const legacyInterface = targetKey.match(/^([^/]+)\/interface\/(.+)$/i);
    return legacyInterface ? `${legacyInterface[2]} on ${legacyInterface[1]}` : targetKey;
  }
  const device = targetNames.length === 1 ? targetNames[0] : fields.device || 'device';
  switch (fields.kind) {
    case 'device_interface':
      return `${fields.interface} on ${device}`;
    case 'device_interface_vlan':
      return `VLAN ${fields.vlan} on ${fields.interface} · ${device}`;
    case 'device_vlan':
      return `VLAN ${fields.vlan} on ${device}`;
    case 'device_svi_subnet':
      return `${fields.interface} in VRF ${fields.vrf} (${fields.subnet}) · ${device}`;
    case 'device_vrf_prefix':
      return `${fields.prefix} in VRF ${fields.vrf} · ${device}`;
    case 'firewall_rule':
      return `Rule ${fields.rule} in policy ${fields.policy} · ${device}`;
    case 'nat_rule':
      return `NAT rule ${fields.nat_rule} in ${fields.policy_domain} · ${device}`;
    case 'acl_entry':
      return `ACE ${fields.ace} in ACL ${fields.acl} · ${fields.direction} on ${fields.attachment} · ${device}`;
    default:
      return targetNames.length === 1 ? targetNames[0] : targetKey;
  }
};

const collectionStatusForTest = (
  observation: ChangeValidationObservationschema | undefined,
  stageRunStatus: string | undefined,
  loading: boolean
): string => {
  if (observation) {
    return observation.status;
  }
  if (!stageRunStatus) {
    return 'not_started';
  }
  if (loading) {
    return 'loading';
  }
  if (stageRunStatus === 'succeeded') {
    return 'no_evidence';
  }
  return stageRunStatus;
};

const buildValidationSummaryRows = (
  validation: ChangeValidation,
  preObservations: ChangeValidationObservationschema[],
  postObservations: ChangeValidationObservationschema[],
  comparisons: ChangeValidationComparisonschema[],
  loading: boolean
): ValidationSummaryRow[] => {
  const currentPreObservations = preObservations.filter((item) => item.validationId === validation.id);
  const currentPostObservations = postObservations.filter((item) => item.validationId === validation.id);
  const currentComparisons = comparisons.filter((item) => item.validationId === validation.id);
  const readinessChecks = validation.latestPreStageRun?.preReadiness?.checks ?? [];
  const preByTest = new Map(currentPreObservations.map((item) => [validationTestKey(item.testcaseId, item.targetKey), item]));
  const postByTest = new Map(currentPostObservations.map((item) => [validationTestKey(item.testcaseId, item.targetKey), item]));
  const comparisonByTest = new Map(currentComparisons.map((item) => [validationTestKey(item.testcaseId, item.targetKey), item]));
  const readinessByTest = new Map(readinessChecks.map((item) => [validationTestKey(item.testcaseId, item.targetKey), item]));
  const orderedKeys = Array.from(new Set([
    ...readinessChecks.map((item) => validationTestKey(item.testcaseId, item.targetKey)),
    ...currentComparisons.map((item) => validationTestKey(item.testcaseId, item.targetKey)),
    ...currentPreObservations.map((item) => validationTestKey(item.testcaseId, item.targetKey)),
    ...currentPostObservations.map((item) => validationTestKey(item.testcaseId, item.targetKey))
  ]));

  if (orderedKeys.length === 0) {
    return [{
      key: validation.selectedProfile?.profileId ?? validation.id,
      label: humanize(validation.selectedProfile?.profileId ?? 'Validation checks'),
      description: validation.summary,
      target: changeTargetNames(validation).join(', ') || 'Scope pending',
      preStatus: validation.latestPreStageRun?.status ?? 'not_started',
      postStatus: validation.latestPostStageRun?.status ?? 'not_started',
      result: validation.finalVerdict ?? 'pending'
    }];
  }

  return orderedKeys.map((key) => {
    const pre = preByTest.get(key);
    const post = postByTest.get(key);
    const comparison = comparisonByTest.get(key);
    const readiness = readinessByTest.get(key);
    const representative = comparison ?? readiness ?? post ?? pre;
    const testcaseId = representative?.testcaseId ?? 'validation_check';
    const targetKey = representative?.targetKey ?? '—';
    return {
      key,
      label: pre?.testcaseName || post?.testcaseName || humanize(testcaseId),
      description: comparison?.summary || validationTestDescriptions[testcaseId] || `Validate ${humanize(testcaseId).toLowerCase()}.`,
      target: friendlyValidationTarget(targetKey, validation),
      preStatus: readiness?.verdict ?? collectionStatusForTest(pre, validation.latestPreStageRun?.status, loading),
      preExpected: readiness?.expectedValue,
      preObserved: readiness?.observedValue,
      preReason: readiness?.reason,
      postStatus: collectionStatusForTest(post, validation.latestPostStageRun?.status, loading),
      result: comparison?.verdict ?? 'pending'
    };
  });
};

const ValidationSummaryTable = ({ rows }: { rows: ValidationSummaryRow[] }) => (
  <div className="change-management-summary__table-wrap">
    <table className="change-management-summary__table" aria-label="Current validation summary">
      <thead>
        <tr>
          <th>Test / validation</th>
          <th>Target</th>
          <th>Pre-check</th>
          <th>Post-check</th>
          <th>Result</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <td>
              <div className="change-management-summary__test">
                <strong>{row.label}</strong>
                <small>{row.description}</small>
              </div>
            </td>
            <td><span className="change-management-summary__target">{row.target}</span></td>
            <td>
              <div className="change-management-summary__precheck">
                <span className={badgeTone(row.preStatus)}>{humanize(row.preStatus)}</span>
                {row.preExpected !== undefined ? (
                  <small>Expected {displayValue(row.preExpected)} · Observed {displayValue(row.preObserved)}</small>
                ) : null}
                {row.preReason ? <small>{row.preReason}</small> : null}
              </div>
            </td>
            <td><span className={badgeTone(row.postStatus)}>{humanize(row.postStatus)}</span></td>
            <td><span className={badgeTone(row.result)}>{humanize(row.result)}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PreReadinessOverview = ({ readiness }: { readiness: PreReadiness }) => {
  const statusCopy: Record<PreReadiness['status'], string> = {
    ready: 'All blocking baseline requirements are satisfied. The proposed change can proceed.',
    attention: 'Blocking requirements are satisfied, but one or more advisory checks need attention.',
    blocked: 'A blocking baseline requirement failed. Correct the baseline or revise the plan, then rerun pre-checks.',
    inconclusive: 'A blocking baseline requirement could not be verified. Resolve the evidence gap, then rerun pre-checks.'
  };
  return (
    <section className={`change-management-readiness change-management-readiness--${readiness.status}`} aria-label="Pre-change readiness">
      <div className="change-management-readiness__heading">
        <div>
          <span>Pre-change readiness</span>
          <strong>{humanize(readiness.status)}</strong>
        </div>
        <span className={badgeTone(readiness.status)}>{humanize(readiness.status)}</span>
      </div>
      <p>{statusCopy[readiness.status]}</p>
      <div className="change-management-readiness__counts">
        {(['pass', 'warn', 'fail', 'inconclusive'] as const).map((verdict) => (
          <span key={verdict}><strong>{readiness.counts[verdict] ?? 0}</strong> {humanize(verdict)}</span>
        ))}
      </div>
    </section>
  );
};

const targetSearchText = (validation: ChangeValidation): string => changeTargetNames(validation).join(' ').toLowerCase();

const isCurrentWeek = (value: Date): boolean => {
  const now = new Date();
  const start = new Date(now);
  const day = start.getDay();
  start.setDate(start.getDate() - ((day + 6) % 7));
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return value >= start && value < end;
};

const ChangeStageIndicator = ({ validation }: { validation: ChangeValidation }) => {
  const stage = changeStage(validation);
  const failed = validation.status === 'failed';
  return (
    <div className="change-management__stage" aria-label={`Stage: ${stage.label}`}>
      <div className="change-management__stage-dots" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((step) => (
          <span
            key={step}
            className={[
              'change-management__stage-dot',
              step <= stage.completed ? 'is-complete' : '',
              step === stage.completed + 1 && stage.completed < 5 ? 'is-current' : '',
              failed && step === stage.completed + 1 ? 'is-failed' : ''
            ].filter(Boolean).join(' ')}
          />
        ))}
      </div>
      <span>{stage.label}</span>
    </div>
  );
};

const TargetChips = ({ validation }: { validation: ChangeValidation }) => {
  const targets = changeTargetNames(validation);
  if (targets.length === 0) {
    return <span className="change-management__muted">Resolving from plan</span>;
  }
  const visible = targets.slice(0, 2);
  return (
    <div className="change-management__targets">
      {visible.map((target) => <span key={target}>{target}</span>)}
      {targets.length > visible.length ? <small>+{targets.length - visible.length}</small> : null}
    </div>
  );
};

const ChangeValidationDrawer = ({
  validation,
  onClose,
  onApprovePlan,
  onRunCollection,
  runningAction,
  actionError,
  planLoading,
  planError,
  api
}: {
  validation: ChangeValidation | null;
  onClose: () => void;
  onApprovePlan: () => void;
  onRunCollection: (stage: CollectionStage) => void;
  runningAction: ChangeAction | null;
  actionError: string | null;
  planLoading: boolean;
  planError: string | null;
  api: ChangeValidationsApiClass;
}) => {
  const orgId = useActiveOrgId();
  const artifactApi = useChatRunsApi();
  const [preObservations, setPreObservations] = useState<ChangeValidationObservationschema[]>([]);
  const [postObservations, setPostObservations] = useState<ChangeValidationObservationschema[]>([]);
  const [comparisons, setComparisons] = useState<ChangeValidationComparisonschema[]>([]);
  const [report, setReport] = useState<ChangeValidationReport | null>(null);
  const [reportHistory, setReportHistory] = useState<ChangeValidationReportSummaryschema[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const targets = validation ? changeTargetNames(validation) : [];
  const stage = validation ? changeStage(validation) : null;
  const collectionRunning = validation?.status === 'pre_running' || validation?.status === 'post_running';
  const canApprovePlan = Boolean(
    !collectionRunning
    && validation?.status === 'plan_ready'
    && validation.links.approvePlan
    && validation.currentPlan?.document
  );
  const canRunPre = Boolean(!collectionRunning && validation?.links.startPreCollection && validation.currentPlan);
  const preReadinessStatus = validation?.latestPreStageRun?.preReadiness?.status;
  const readinessAllowsPost = !preReadinessStatus || preReadinessStatus === 'ready' || preReadinessStatus === 'attention';
  const canRunPost = Boolean(!collectionRunning && readinessAllowsPost && validation?.links.startPostCollection && validation.currentPlan && validation.latestPreStageRun);
  const selectedReportSummary = reportHistory.find((item) => item.reportId === selectedReportId)
    ?? (validation?.latestReport?.reportId === selectedReportId ? validation.latestReport : null);
  const displayedPreStageRunId = selectedReportSummary?.preStageRunId ?? validation?.latestPreStageRun?.stageRunId;
  const displayedPostStageRunId = selectedReportSummary?.postStageRunId ?? validation?.latestPostStageRun?.stageRunId;
  const summaryRows = useMemo(() => validation
    ? buildValidationSummaryRows(validation, preObservations, postObservations, comparisons, evidenceLoading)
    : [], [validation, preObservations, postObservations, comparisons, evidenceLoading]);

  useEffect(() => {
    let cancelled = false;
    const latest = validation?.latestReport;
    setReportHistory(latest ? [latest] : []);
    setSelectedReportId(latest?.reportId ?? null);
    if (!orgId || !validation || !latest) {
      return () => { cancelled = true; };
    }
    api.listChangeValidationReports({
      orgId, validationId: validation.id, limit: 100
    }, { cache: 'no-store' }).then((response) => {
      if (cancelled) {
        return;
      }
      const ordered = [...response.items].sort((left, right) => right.version - left.version);
      setReportHistory(ordered);
      setSelectedReportId((current) => ordered.some((item) => item.reportId === current)
        ? current
        : ordered[0]?.reportId ?? null);
    }).catch(() => {
      // The current report remains usable if loading older immutable reports fails.
    });
    return () => { cancelled = true; };
  }, [api, orgId, validation?.id, validation?.latestReport?.reportId]);

  useEffect(() => {
    let cancelled = false;
    setPreObservations([]);
    setPostObservations([]);
    setComparisons([]);
    setReport(null);
    setEvidenceError(null);
    if (!orgId || !validation) {
      setEvidenceLoading(false);
      return () => { cancelled = true; };
    }
    const requests: Array<Promise<void>> = [];
    setEvidenceLoading(Boolean(displayedPreStageRunId || displayedPostStageRunId || selectedReportSummary));
    if (displayedPreStageRunId) {
      requests.push(api.listChangeValidationObservations({
        orgId, validationId: validation.id, stageRunId: displayedPreStageRunId
      }, { cache: 'no-store' }).then((response) => { if (!cancelled) setPreObservations(response.items); }));
    }
    if (displayedPostStageRunId) {
      requests.push(api.listChangeValidationObservations({
        orgId, validationId: validation.id, stageRunId: displayedPostStageRunId
      }, { cache: 'no-store' }).then((response) => { if (!cancelled) setPostObservations(response.items); }));
      requests.push(api.listChangeValidationComparisons({
        orgId, validationId: validation.id, postStageRunId: displayedPostStageRunId, limit: 100
      }, { cache: 'no-store' }).then((response) => { if (!cancelled) setComparisons(response.items); }));
    }
    if (selectedReportSummary) {
      requests.push(api.getChangeValidationReport({
        orgId, validationId: validation.id, reportId: selectedReportSummary.reportId
      }, { cache: 'no-store' }).then((response) => { if (!cancelled) setReport(response); }));
    }
    Promise.all(requests)
      .catch((caught) => {
        if (!cancelled) setEvidenceError(caught instanceof Error ? caught.message : 'Unable to load collection evidence.');
      })
      .finally(() => { if (!cancelled) setEvidenceLoading(false); });
    return () => { cancelled = true; };
  }, [api, orgId, validation?.id, displayedPreStageRunId, displayedPostStageRunId, selectedReportSummary?.reportId]);

  return (
    <Drawer
      open={Boolean(validation)}
      title={validation ? changeReference(validation) : 'Change validation'}
      subtitle={validation ? validation.summary : undefined}
      onClose={onClose}
      width="wide"
      footer={(
        <div className="change-management-detail__actions">
          <button type="button" className="ui-button ui-button--ghost" onClick={onClose}>Close</button>
          {validation?.status === 'plan_ready' && validation.links.approvePlan ? (
            <button
              type="button"
              className="ui-button ui-button--primary"
              disabled={runningAction !== null || !canApprovePlan}
              onClick={onApprovePlan}
            >
              {runningAction === 'approve-plan' ? 'Approving plan…' : planLoading ? 'Loading plan…' : 'Approve plan'}
            </button>
          ) : null}
          {canRunPre ? (
            <button
              type="button"
              className="ui-button ui-button--secondary"
              disabled={runningAction !== null}
              onClick={() => onRunCollection('pre')}
            >
              {runningAction === 'pre' ? 'Starting pre-checks…' : 'Run pre-checks'}
            </button>
          ) : null}
          {canRunPost ? (
            <button
              type="button"
              className="ui-button ui-button--primary"
              disabled={runningAction !== null}
              onClick={() => onRunCollection('post')}
            >
              {runningAction === 'post' ? 'Starting post-checks…' : 'Run post-checks'}
            </button>
          ) : null}
        </div>
      )}
    >
      {validation ? (
        <div className="change-management-detail">
          <section className="change-management-summary">
            <div className="change-management-summary__heading">
              <div>
                <span className="change-management-detail__eyebrow">Current summary</span>
                <h3>Validation status</h3>
              </div>
              <div className="change-management-detail__status-row">
                <span className={badgeTone(validation.status)}>{humanize(validation.status)}</span>
                <span className={badgeTone(validation.finalVerdict)}>{verdictLabel(validation.finalVerdict)}</span>
              </div>
            </div>
            <div className="change-management-summary__context">
              <span>{humanize(validation.selectedProfile?.profileId)}</span>
              <span>{targets.join(', ') || 'Target scope pending'}</span>
              <span>Updated {formatDateTime(validation.updatedAt)}</span>
            </div>
            <ChangeStageIndicator validation={validation} />
            {actionError ? <div className="org-shell__banner" role="alert">{actionError}</div> : null}
            {planError ? <div className="org-shell__banner" role="alert">Unable to load the exact plan for review. {planError}</div> : null}
            {validation.currentPlan?.document ? <PlanReview validation={validation} document={validation.currentPlan.document} /> : null}
            {planLoading ? <div className="change-management-detail__loading">Loading the exact validation plan…</div> : null}
            {validation.latestPreStageRun?.preReadiness ? <PreReadinessOverview readiness={validation.latestPreStageRun.preReadiness} /> : null}
            <ValidationSummaryTable rows={summaryRows} />
            {evidenceLoading ? <div className="change-management-detail__loading">Refreshing current test status…</div> : null}
          </section>

          <details className="change-management-detail__fold">
            <summary>
              <span>Validation details and evidence</span>
              <small>Scope, collection attempts, evidence, comparisons, report, and audit data</small>
            </summary>
            <div className="change-management-detail__fold-content">
              <section>
                <h3>Change scope</h3>
                <dl className="change-management-detail__grid">
                  <div><dt>Profile</dt><dd>{humanize(validation.selectedProfile?.profileId)}</dd></div>
                  <div><dt>Profile version</dt><dd>{validation.selectedProfile?.profileVersion ?? '—'}</dd></div>
                  <div><dt>Plan</dt><dd>{validation.currentPlan ? `Version ${validation.currentPlan.version} · ${humanize(validation.currentPlan.status)}` : 'Not generated'}</dd></div>
                  <div><dt>Source</dt><dd>{sourceLabel(validation)}</dd></div>
                </dl>
                <div className="change-management-detail__target-list">
                  <span>Targets</span>
                  {targets.length > 0 ? <TargetChips validation={validation} /> : <strong>Resolved when the plan is generated</strong>}
                </div>
              </section>

              <section>
                <h3>Collection status</h3>
                <div className="change-management-detail__runs">
                  <article>
                    <span>Pre-checks</span>
                    <strong>{validation.latestPreStageRun ? humanize(validation.latestPreStageRun.status) : 'Not started'}</strong>
                    <small>{validation.latestPreStageRun ? `Attempt ${validation.latestPreStageRun.attempt} · ${formatDateTime(validation.latestPreStageRun.endedAt ?? validation.latestPreStageRun.requestedAt)}` : 'Baseline has not been captured.'}</small>
                    {validation.latestPreStageRun?.preReadiness ? (
                      <small>Readiness: {humanize(validation.latestPreStageRun.preReadiness.status)}</small>
                    ) : null}
                  </article>
                  <article>
                    <span>Post-checks</span>
                    <strong>{validation.latestPostStageRun ? humanize(validation.latestPostStageRun.status) : 'Not started'}</strong>
                    <small>{validation.latestPostStageRun ? `Attempt ${validation.latestPostStageRun.attempt} · ${formatDateTime(validation.latestPostStageRun.endedAt ?? validation.latestPostStageRun.requestedAt)}` : 'User-triggered post-checks have not run.'}</small>
                  </article>
                </div>
              </section>

              {reportHistory.length > 0 ? (
                <section className="change-management-report-history">
                  <h3>Validation run history</h3>
                  <label>
                    <span>Displayed run</span>
                    <select
                      aria-label="Displayed validation run"
                      value={selectedReportId ?? ''}
                      onChange={(event) => setSelectedReportId(event.target.value)}
                    >
                      {reportHistory.map((item, index) => (
                        <option key={item.reportId} value={item.reportId}>
                          {`Version ${item.version} · ${humanize(item.finalVerdict)} · ${formatDateTime(item.createdAt)}${index === 0 ? ' · Latest' : ''}`}
                        </option>
                      ))}
                    </select>
                  </label>
                </section>
              ) : null}

              {evidenceError ? <div className="org-shell__banner" role="alert">Unable to load all collection details. {evidenceError}</div> : null}
              {displayedPreStageRunId && !evidenceLoading ? (
                <ObservationEvidence artifactApi={artifactApi} orgId={orgId!} title="Pre-check evidence" emptyMessage="This run did not persist any normalized observations." observations={preObservations} />
              ) : null}
              {displayedPostStageRunId && !evidenceLoading ? (
                <ObservationEvidence artifactApi={artifactApi} orgId={orgId!} title="Post-check evidence" emptyMessage="This run did not persist any normalized observations." observations={postObservations} />
              ) : null}
              {displayedPostStageRunId && !evidenceLoading ? <ComparisonEvidence comparisons={comparisons} /> : null}

              {report ? (
                <section className="change-management-report">
                  <div className="change-management-evidence__heading">
                    <h3>Validation report</h3>
                    <span className={badgeTone(report.finalVerdict)}>{verdictLabel(report.finalVerdict)}</span>
                  </div>
                  <div className="change-management-report__counts">
                    {Object.entries(report.comparisonSummary).map(([verdict, count]) => (
                      <div key={verdict}><strong>{count}</strong><span>{humanize(verdict)}</span></div>
                    ))}
                  </div>
                  {report.verdictReasons.length > 0 ? (
                    <div><h4>Verdict reasons</h4><ul>{report.verdictReasons.map((reason) => <li key={reason}>{reason}</li>)}</ul></div>
                  ) : null}
                  {report.recommendedNextActions.length > 0 ? (
                    <div><h4>Recommended next actions</h4><ul>{report.recommendedNextActions.map((action) => <li key={action}>{action}</li>)}</ul></div>
                  ) : null}
                </section>
              ) : null}

              <section>
                <h3>Audit details</h3>
                <dl className="change-management-detail__grid">
                  <div><dt>Created</dt><dd>{formatDateTime(validation.createdAt)}</dd></div>
                  <div><dt>Updated</dt><dd>{formatDateTime(validation.updatedAt)}</dd></div>
                  <div><dt>Created by</dt><dd>{validation.createdBy || '—'}</dd></div>
                  <div><dt>State version</dt><dd>{validation.stateVersion}</dd></div>
                </dl>
              </section>

              <details className="change-management-detail__technical">
                <summary>Technical identifiers</summary>
                <dl>
                  <div><dt>Validation ID</dt><dd><code>{validation.id}</code></dd></div>
                  {validation.currentPlan ? <div><dt>Plan ID</dt><dd><code>{validation.currentPlan.planId}</code></dd></div> : null}
                  {validation.latestReport ? <div><dt>Report ID</dt><dd><code>{validation.latestReport.reportId}</code></dd></div> : null}
                </dl>
              </details>
            </div>
          </details>
        </div>
      ) : null}
    </Drawer>
  );
};

const ChangeManagementPage = () => {
  const orgId = useActiveOrgId();
  const api = useChangeValidationsApi();
  const { apiBaseUrl } = useConfig();
  const { fetchWithAuthRetry } = useAuthFetch();
  const basePath = useMemo(() => resolveBasePath(apiBaseUrl), [apiBaseUrl]);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const bypassCacheRef = useRef(false);
  const [bucket, setBucket] = useState<ChangeBucket>('all');
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('all');
  const [profile, setProfile] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [selected, setSelected] = useState<ChangeValidation | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [runningAction, setRunningAction] = useState<ChangeAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [newChangeOpen, setNewChangeOpen] = useState(false);
  const [newChangeDescription, setNewChangeDescription] = useState('');
  const [newChangeSubmitting, setNewChangeSubmitting] = useState(false);
  const [newChangeError, setNewChangeError] = useState<string | null>(null);
  const [newChangeProgress, setNewChangeProgress] = useState<NewChangeProgress | null>(null);
  const pendingNewChangeThreadIdRef = useRef<string | null>(null);
  const pendingNewChangeRequestIdRef = useRef<string | null>(null);

  const closeNewChange = () => {
    if (newChangeSubmitting) {
      return;
    }
    setNewChangeOpen(false);
    setNewChangeDescription('');
    setNewChangeError(null);
    setNewChangeProgress(null);
    pendingNewChangeThreadIdRef.current = null;
    pendingNewChangeRequestIdRef.current = null;
  };

  const startNewChangeConversation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const description = newChangeDescription.trim();
    if (!orgId || !description || newChangeSubmitting) {
      return;
    }

    setNewChangeSubmitting(true);
    setNewChangeError(null);
    try {
      let threadId = pendingNewChangeThreadIdRef.current;
      if (!threadId) {
        const threadResponse = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, 'chat/threads'), {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: newChangeConversationTitle(description), visibility: 'private' })
        });
        if (!threadResponse.ok) {
          throw new ApiResponseError(threadResponse, `Unable to create change conversation (${threadResponse.status}).`);
        }
        const thread = (await threadResponse.json()) as { id?: string };
        threadId = thread.id?.trim() ?? '';
        if (!threadId) {
          throw new Error('The new change conversation did not return a thread ID.');
        }
        pendingNewChangeThreadIdRef.current = threadId;
      }

      const clientRequestId = pendingNewChangeRequestIdRef.current ?? newChangeClientRequestId();
      pendingNewChangeRequestIdRef.current = clientRequestId;
      const interactResponse = await fetchWithAuthRetry(
        buildOrgUrl(basePath, orgId, `chat/threads/${encodeURIComponent(threadId)}/interact`),
        {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: [{ type: 'text', text: newChangeConversationPrompt(description) }],
            context: { clientRequestId },
            trace: { level: 'summary' }
          })
        }
      );
      if (!interactResponse.ok) {
        throw new ApiResponseError(interactResponse, `Unable to start change conversation (${interactResponse.status}).`);
      }

      const runId = chatRunIdFromLocation(interactResponse.headers.get('Location'));
      pendingNewChangeRequestIdRef.current = null;
      setNewChangeProgress({
        threadId,
        runId: runId ?? undefined,
        phase: runId ? 'running' : 'finalizing',
        detail: runId ? 'REAP is interpreting the change and generating its validation plan.' : 'The request was accepted. Waiting for the validation record.',
        activity: ['Change request submitted'],
        runFinished: !runId
      });
      bypassCacheRef.current = true;
      setRefreshNonce((value) => value + 1);
    } catch (caught) {
      setNewChangeError(caught instanceof Error ? caught.message : 'Unable to start the change conversation.');
    } finally {
      setNewChangeSubmitting(false);
    }
  };

  const fetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    return () => {
      const bypassCache = bypassCacheRef.current;
      bypassCacheRef.current = false;
      return api.listChangeValidations({ orgId, limit: MAX_VALIDATIONS }, bypassCache ? { cache: 'no-store' } : undefined);
    };
  }, [api, orgId, refreshNonce]);

  const { data, loading, error } = useApiQuery<ChangeValidationListResponse>(fetcher, [fetcher]);
  const validations = data?.items ?? [];
  const truncated = Boolean(data?.meta.next);

  useEffect(() => {
    const runId = newChangeProgress?.runId;
    const threadId = newChangeProgress?.threadId;
    if (!newChangeOpen || !orgId || !runId || !threadId) {
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    const streamUrl = buildOrgUrl(basePath, orgId, `chat/runs/${encodeURIComponent(runId)}/events`);
    void streamSse(streamUrl, {
      onEvent: (eventType, dataValue) => {
        if (cancelled) {
          return;
        }
        let payload: Record<string, unknown> = {};
        try {
          payload = JSON.parse(dataValue) as Record<string, unknown>;
        } catch {
          payload = {};
        }
        const summary = summarizeRunEvent(eventType, payload);
        const status = typeof payload.status === 'string' ? payload.status.trim().toLowerCase() : '';
        const planApproval = changeValidationPlanApprovalFromRunEvent(eventType, payload);
        setNewChangeProgress((current) => {
          if (!current || current.runId !== runId) {
            return current;
          }
          const activity = summary ? appendNewChangeActivity(current.activity, summary) : current.activity;
          if (planApproval) {
            return {
              ...current,
              validationId: planApproval.validationId || current.validationId,
              planApprovalObserved: true,
              phase: 'finalizing',
              detail: 'The validation plan was generated. Loading it here for review and approval.',
              activity
            };
          }
          if (status === 'needs_input') {
            if (current.planApprovalObserved) {
              return {
                ...current, runId: undefined, phase: 'finalizing', runFinished: true, activity,
                detail: 'The validation plan was generated. Loading it here for review and approval.'
              };
            }
            return {
              ...current, runId: undefined, phase: 'needs_input', runFinished: true, activity,
              detail: summary || 'REAP needs additional information before it can finish the plan.'
            };
          }
          if (eventType === 'error' || ['failed', 'cancelled', 'canceled', 'rejected'].includes(status)) {
            return {
              ...current, runId: undefined, phase: 'failed', runFinished: true, activity,
              detail: summary || 'The plan-generation run did not complete.'
            };
          }
          if (['succeeded', 'completed'].includes(status)) {
            return {
              ...current, runId: undefined, phase: 'finalizing', runFinished: true, activity,
              detail: 'Plan generation completed. Refreshing the Change Management record.'
            };
          }
          return {
            ...current,
            phase: 'running',
            detail: summary || current.detail,
            activity
          };
        });
        if (eventType === 'error' || status === 'needs_input' || ['succeeded', 'completed', 'failed', 'cancelled', 'canceled', 'rejected'].includes(status)) {
          bypassCacheRef.current = true;
          setRefreshNonce((value) => value + 1);
        }
      },
      reconnect: { enabled: true, maxAttempts: 2, retryDelayMs: 1_000 }
    }, controller.signal).catch((caught) => {
      if (cancelled || controller.signal.aborted) {
        return;
      }
      setNewChangeProgress((current) => current?.runId === runId ? {
        ...current,
        runId: undefined,
        phase: 'disconnected',
        detail: caught instanceof Error ? caught.message : 'Live progress updates disconnected.',
        runFinished: false
      } : current);
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [basePath, newChangeOpen, newChangeProgress?.runId, newChangeProgress?.threadId, orgId]);

  useEffect(() => {
    if (!newChangeOpen || !newChangeProgress || !['running', 'finalizing'].includes(newChangeProgress.phase)) {
      return;
    }
    const timer = window.setInterval(() => {
      bypassCacheRef.current = true;
      setRefreshNonce((value) => value + 1);
    }, COLLECTION_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [newChangeOpen, newChangeProgress?.phase, newChangeProgress?.threadId]);

  useEffect(() => {
    if (!newChangeProgress) {
      return;
    }
    const validation = validations.find((item) => sourceChatThreadId(item) === newChangeProgress.threadId);
    if (!validation) {
      return;
    }
    setNewChangeProgress((current) => {
      if (!current || current.threadId !== newChangeProgress.threadId) {
        return current;
      }
      if (validation.status === 'failed' || validation.status === 'cancelled') {
        if (current.validationId === validation.id && current.phase === 'failed') {
          return current;
        }
        return {
          ...current, runId: undefined, validationId: validation.id, phase: 'failed', runFinished: true,
          detail: 'The validation could not be prepared. Open the conversation for details.'
        };
      }
      if (validation.currentPlan || validation.status === 'plan_ready') {
        if (current.validationId === validation.id && current.phase === 'ready') {
          return current;
        }
        return {
          ...current, runId: undefined, validationId: validation.id, planApprovalObserved: false, phase: 'ready', runFinished: true,
          detail: 'The validation plan is ready to review on this page.'
        };
      }
      if (validation.status === 'needs_input' || (current.runFinished && validation.status === 'draft' && !current.planApprovalObserved)) {
        if (current.validationId === validation.id && current.phase === 'needs_input') {
          return current;
        }
        return {
          ...current, runId: undefined, validationId: validation.id, phase: 'needs_input', runFinished: true,
          detail: 'REAP needs additional information before it can generate the validation plan.'
        };
      }
      return current.validationId === validation.id ? current : { ...current, validationId: validation.id };
    });
  }, [newChangeProgress?.runFinished, newChangeProgress?.threadId, validations]);

  const sourceOptions = useMemo(() => Array.from(new Set(validations.map(sourceLabel))).sort(), [validations]);
  const profileOptions = useMemo(
    () => Array.from(new Set(validations.map((item) => item.selectedProfile?.profileId).filter((value): value is string => Boolean(value)))).sort(),
    [validations]
  );

  const counts = useMemo(() => ({
    all: validations.length,
    needs_action: validations.filter((item) => changeBucketForStatus(item.status) === 'needs_action').length,
    in_progress: validations.filter((item) => changeBucketForStatus(item.status) === 'in_progress').length,
    completed: validations.filter((item) => changeBucketForStatus(item.status) === 'completed').length,
    thisWeek: validations.filter((item) => isCurrentWeek(item.createdAt)).length,
    verdicts: validations.filter((item) => Boolean(item.finalVerdict)).length
  }), [validations]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return validations.filter((validation) => {
      if (bucket !== 'all' && changeBucketForStatus(validation.status) !== bucket) {
        return false;
      }
      if (source !== 'all' && sourceLabel(validation) !== source) {
        return false;
      }
      if (profile !== 'all' && validation.selectedProfile?.profileId !== profile) {
        return false;
      }
      if (!query) {
        return true;
      }
      return [
        changeReference(validation), validation.summary, sourceLabel(validation), validation.selectedProfile?.profileId ?? '',
        targetSearchText(validation), validation.status, validation.finalVerdict ?? ''
      ].some((value) => value.toLowerCase().includes(query));
    });
  }, [bucket, profile, search, source, validations]);

  useEffect(() => setPage(1), [bucket, profile, search, source]);
  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [filtered.length, page, pageSize]);

  const rows = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));

  useEffect(() => {
    if (!selected || !data) {
      return;
    }
    const refreshed = data.items.find((validation) => validation.id === selected.id);
    if (refreshed) {
      setSelected((current) => mergeChangeValidationDetail(current, refreshed));
    }
  }, [data]);

  useEffect(() => {
    if (!orgId || !selected?.currentPlan) {
      setPlanLoading(false);
      setPlanError(null);
      return;
    }
    if (selected.currentPlan.document) {
      setPlanLoading(false);
      setPlanError(null);
      return;
    }

    let cancelled = false;
    setPlanLoading(true);
    setPlanError(null);
    api.getChangeValidation({ orgId, validationId: selected.id }, { cache: 'no-store' })
      .then((detail) => {
        if (!cancelled) {
          setSelected((current) => current?.id === detail.id ? mergeChangeValidationDetail(current, detail) : current);
          if (!detail.currentPlan?.document) {
            setPlanError('The server response did not include the frozen plan document.');
          }
        }
      })
      .catch((caught) => {
        if (!cancelled) {
          setPlanError(caught instanceof Error ? caught.message : 'The plan detail request failed.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPlanLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [api.getChangeValidation, orgId, selected?.id, selected?.currentPlan?.planId, selected?.currentPlan?.version]);

  useEffect(() => {
    if (!orgId || !selected || !IN_PROGRESS_STATUSES.has(selected.status)) {
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    const pollValidation = async () => {
      try {
        const refreshed = await api.getChangeValidation({
          orgId,
          validationId: selected.id
        }, { cache: 'no-store' });
        if (cancelled) {
          return;
        }
        setSelected((current) => current?.id === refreshed.id ? mergeChangeValidationDetail(current, refreshed) : current);
        if (!IN_PROGRESS_STATUSES.has(refreshed.status)) {
          bypassCacheRef.current = true;
          setRefreshNonce((value) => value + 1);
          return;
        }
      } catch {
        // A transient polling failure should not replace an actionable error
        // from the collection request or close the validation drawer.
      }
      if (!cancelled) {
        timer = window.setTimeout(pollValidation, COLLECTION_POLL_INTERVAL_MS);
      }
    };

    timer = window.setTimeout(pollValidation, COLLECTION_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [api, orgId, selected?.id, selected?.status]);

  const approvePlan = async () => {
    if (!selected?.currentPlan?.document || selected.status !== 'plan_ready' || !selected.links.approvePlan || !orgId || runningAction) {
      return;
    }

    setRunningAction('approve-plan');
    setActionError(null);
    try {
      const operation = await api.approveChangeValidationPlan({
        orgId,
        validationId: selected.id,
        idempotencyKey: changeValidationIdempotencyKey('approve-plan'),
        changeValidationApprovePlanRequest: {
          expectedStateVersion: selected.stateVersion,
          planId: selected.currentPlan.planId,
          planVersion: selected.currentPlan.version
        }
      });

      setSelected((current) => {
        if (current?.id !== selected.id) {
          return current;
        }
        const { approvePlan: _approvedCapability, ...remainingLinks } = current.links;
        return {
          ...current,
          status: 'approved',
          stateVersion: operation.stateVersion,
          currentPlan: current.currentPlan ? { ...current.currentPlan, status: 'approved' } : current.currentPlan,
          links: remainingLinks
        };
      });
      bypassCacheRef.current = true;
      setRefreshNonce((value) => value + 1);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Unable to approve the change-validation plan.');
    } finally {
      setRunningAction(null);
    }
  };

  const runCollection = async (stage: CollectionStage) => {
    if (!selected?.currentPlan || !orgId || runningAction) {
      return;
    }
    if (stage === 'post' && !selected.latestPreStageRun) {
      setActionError('Run pre-checks before starting post-checks.');
      return;
    }

    setRunningAction(stage);
    setActionError(null);
    try {
      const common = {
        orgId,
        validationId: selected.id,
        idempotencyKey: changeValidationIdempotencyKey(stage)
      };
      const operation = stage === 'pre'
        ? await api.startChangeValidationPreCollection({
            ...common,
            changeValidationStartPreCollectionRequest: {
              expectedStateVersion: selected.stateVersion,
              planId: selected.currentPlan.planId,
              planVersion: selected.currentPlan.version
            }
          })
        : await api.startChangeValidationPostCollection({
            ...common,
            changeValidationStartPostCollectionRequest: {
              expectedStateVersion: selected.stateVersion,
              planId: selected.currentPlan.planId,
              planVersion: selected.currentPlan.version,
              preStageRunId: selected.latestPreStageRun!.stageRunId
            }
          });

      setSelected((current) => current?.id === selected.id
        ? { ...current, status: stage === 'pre' ? 'pre_running' : 'post_running', stateVersion: operation.stateVersion }
        : current);
      bypassCacheRef.current = true;
      setRefreshNonce((value) => value + 1);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : `Unable to start ${stage}-checks.`);
    } finally {
      setRunningAction(null);
    }
  };

  const columns = useMemo<Array<ListWorkspaceColumn<ChangeValidation>>>(() => [
    {
      key: 'change', header: 'Change', width: '9rem', render: (row) => (
        <div className="change-management__identity">
          <strong>{changeReference(row)}</strong>
          <span>{sourceLabel(row)}</span>
        </div>
      )
    },
    { key: 'summary', header: 'Title', width: '22rem', render: (row) => <span className="change-management__summary">{row.summary}</span> },
    { key: 'profile', header: 'REAP change type', width: '13rem', render: (row) => humanize(row.selectedProfile?.profileId) },
    { key: 'targets', header: 'Targets', width: '13rem', render: (row) => <TargetChips validation={row} /> },
    { key: 'updated', header: 'Updated', width: '11rem', render: (row) => <span className="change-management__muted">{formatDateTime(row.updatedAt)}</span> },
    { key: 'stage', header: 'Stage', width: '12rem', render: (row) => <ChangeStageIndicator validation={row} /> },
    { key: 'verdict', header: 'Verdict', width: '10rem', render: (row) => <span className={badgeTone(row.finalVerdict)}>{verdictLabel(row.finalVerdict)}</span> }
  ], []);

  if (!orgId) {
    return (
      <OrgModuleLayout title="Change Management" description="Validate network changes before and after implementation.">
        <OrgModulePlaceholder intent="Select an organization to load change validations." />
      </OrgModuleLayout>
    );
  }

  const tabs = [
    { id: 'all', label: 'All', badge: <span className="change-management__tab-count">{counts.all}</span> },
    { id: 'needs_action', label: 'Needs action', badge: <span className="change-management__tab-count">{counts.needs_action}</span> },
    { id: 'in_progress', label: 'In progress', badge: <span className="change-management__tab-count">{counts.in_progress}</span> },
    { id: 'completed', label: 'Completed', badge: <span className="change-management__tab-count">{counts.completed}</span> }
  ];
  const createdValidation = newChangeProgress?.validationId
    ? validations.find((validation) => validation.id === newChangeProgress.validationId)
    : undefined;
  const newChangeChatPath = newChangeProgress
    ? buildOrgScopedPath('/app/chat-agents', orgId, new URLSearchParams({ threadId: newChangeProgress.threadId }))
    : undefined;
  const openCreatedValidation = () => {
    if (!createdValidation) {
      return;
    }
    setSelected(createdValidation);
    closeNewChange();
  };

  return (
    <>
      <OrgModuleLayout
        title="Change Management"
        description="Validate changes before and after implementation, classify every delta, and retain the evidence."
        actions={
          <button
            type="button"
            className="ui-button ui-button--primary"
            onClick={() => {
              setNewChangeError(null);
              setNewChangeOpen(true);
            }}
          >
            <PlusIcon size={17} /> New change
          </button>
        }
        topSlot={
          <div className="kpi-grid change-management__metrics">
            <KpiCard label="Changes this week" value={loading && !data ? '…' : formatNumber(counts.thisWeek)} secondary={truncated ? `from latest ${MAX_VALIDATIONS}` : 'in this organization'} />
            <KpiCard label="Needs action" value={loading && !data ? '…' : formatNumber(counts.needs_action)} secondary="confirm a plan or run checks" accent={counts.needs_action > 0 ? 'warning' : 'default'} onClick={() => setBucket('needs_action')} />
            <KpiCard label="In progress" value={loading && !data ? '…' : formatNumber(counts.in_progress)} secondary="collection running now" onClick={() => setBucket('in_progress')} />
            <KpiCard label="Verdicts returned" value={loading && !data ? '…' : formatNumber(counts.verdicts)} secondary="post-check reports available" accent="success" onClick={() => setBucket('completed')} />
          </div>
        }
      >
        <div className="change-management__workspace">
          {error ? <div className="org-shell__banner" role="alert">{changeManagementErrorMessage(error)}</div> : null}
          <ListWorkspace
            rows={rows}
            columns={columns}
            rowId={(row) => row.id}
            page={page}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            paginationMeta={{
              totalCount: filtered.length,
              pageCount,
              hasNextPage: page < pageCount,
              hasPrevPage: page > 1,
              exactTotal: filtered.length
            }}
            loading={loading}
            emptyState={<p>No change validations match the current view.</p>}
            tabs={tabs}
            activeTabId={bucket}
            onTabChange={(value) => setBucket(value as ChangeBucket)}
            selectionDisabled
            onRowClick={(validation) => {
              setActionError(null);
              setSelected(validation);
            }}
            onRefresh={() => {
              bypassCacheRef.current = true;
              setRefreshNonce((value) => value + 1);
            }}
            toolbarLeadingActions={
              <div className="change-management__filters" aria-label="Change validation filters">
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search changes or targets"
                  aria-label="Search change validations"
                />
                <select value={source} onChange={(event) => setSource(event.target.value)} aria-label="Filter by source">
                  <option value="all">All sources</option>
                  {sourceOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
                <select value={profile} onChange={(event) => setProfile(event.target.value)} aria-label="Filter by change type">
                  <option value="all">All change types</option>
                  {profileOptions.map((value) => <option key={value} value={value}>{humanize(value)}</option>)}
                </select>
              </div>
            }
            headerStatus={truncated ? <span>Showing the latest {MAX_VALIDATIONS} validations.</span> : undefined}
          />
          <p className="change-management__footnote">Read-only validation. REAP collects evidence but never pushes device configuration.</p>
        </div>
      </OrgModuleLayout>
      <ChangeValidationDrawer
        validation={selected}
        onClose={() => {
          setActionError(null);
          setSelected(null);
        }}
        onApprovePlan={approvePlan}
        onRunCollection={runCollection}
        runningAction={runningAction}
        actionError={actionError}
        planLoading={planLoading}
        planError={planError}
        api={api}
      />
      <Drawer
        open={newChangeOpen}
        title="New change validation"
        subtitle={newChangeProgress
          ? 'REAP is preparing the validation while you remain in Change Management.'
          : 'Describe the proposed change. REAP will resolve the scope and prepare the validation plan.'}
        onClose={closeNewChange}
        ariaDescribedBy="new-change-description-help"
        footer={
          newChangeProgress ? (
            <>
              <button type="button" className="ui-button ui-button--ghost" onClick={closeNewChange}>Close</button>
              {newChangeProgress.phase === 'ready' && createdValidation ? (
                <button type="button" className="ui-button ui-button--primary" onClick={openCreatedValidation}>
                  Review validation
                </button>
              ) : null}
              {newChangeProgress.phase === 'needs_input' && newChangeChatPath ? (
                <a className="ui-button ui-button--primary" href={newChangeChatPath}>Provide input in chat</a>
              ) : null}
              {['failed', 'disconnected'].includes(newChangeProgress.phase) && newChangeChatPath ? (
                <a className="ui-button ui-button--secondary" href={newChangeChatPath}>View conversation</a>
              ) : null}
            </>
          ) : (
            <>
              <button type="button" className="ui-button ui-button--ghost" onClick={closeNewChange} disabled={newChangeSubmitting}>
                Cancel
              </button>
              <button
                type="submit"
                form="new-change-validation-form"
                className="ui-button ui-button--primary"
                disabled={newChangeSubmitting || newChangeDescription.trim().length === 0}
              >
                {newChangeSubmitting ? 'Starting…' : 'Start change validation'}
              </button>
            </>
          )
        }
      >
        {newChangeProgress ? (
          <section className="change-management-new__progress" aria-live="polite">
            <div className="change-management-new__progress-heading">
              <span className={`change-management-new__progress-dot is-${newChangeProgress.phase}`} aria-hidden="true" />
              <div>
                <strong>{newChangeProgressTitle(newChangeProgress.phase)}</strong>
                <p>{newChangeProgress.detail}</p>
              </div>
            </div>
            <div className="change-management-new__request">
              <span>Proposed change</span>
              <p>{newChangeDescription}</p>
            </div>
            {newChangeProgress.activity.length > 0 ? (
              <div className="change-management-new__activity">
                <span>Progress</span>
                <ol>
                  {newChangeProgress.activity.map((item, index) => <li key={`${index}-${item}`}>{item}</li>)}
                </ol>
              </div>
            ) : null}
            {newChangeProgress.phase === 'needs_input' ? (
              <div className="change-management-new__conversation-note">
                <strong>Operator input required</strong>
                <span>Use the chat link below to answer REAP’s specific follow-up question. Return here afterward to review the plan.</span>
              </div>
            ) : null}
          </section>
        ) : (
          <form id="new-change-validation-form" className="change-management-new" onSubmit={startNewChangeConversation}>
            <label htmlFor="new-change-description">Proposed change</label>
            <textarea
              id="new-change-description"
              value={newChangeDescription}
              onChange={(event) => setNewChangeDescription(event.target.value)}
              rows={9}
              autoFocus
              disabled={newChangeSubmitting}
              placeholder="Example: Change interface Ethernet1 on CAMPUSB-DIST-A2-S10 from administratively down to up."
            />
            <p id="new-change-description-help">
              Include the device name, affected interface, policy, route, VLAN, NAT rule, or ACL and the intended outcome when known. You do not need a device UUID.
            </p>
            <div className="change-management-new__conversation-note">
              <strong>What happens next</strong>
              <span>Progress remains on this page. A chat link appears only if REAP needs additional input.</span>
            </div>
            {newChangeError ? <div className="org-shell__banner" role="alert">{newChangeError}</div> : null}
          </form>
        )}
      </Drawer>
    </>
  );
};

export default ChangeManagementPage;
